import type { Workspace } from "@storyboard/web-client/auth";

/** The segment demo mode adds to the local data scope, so its labeled rows never share a database with real names. */
const DEMO_SCOPE_SEGMENT = "demo";

/**
 * Whether demo mode is on for the workspace a page boots into: `web.demoMode` is exactly `true` in its config as the
 * `/userinfo` response carries it.
 *
 * Read once at boot, from the same response the local data scope is built from, so the scope and the labels the
 * store writes always agree; a config change takes effect on the next page load.
 */
export function demoModeAtBoot(workspaces: readonly Workspace[], workspaceId: string | null): boolean {
  if (!workspaceId) return false;
  return workspaces.find((workspace) => workspace.id === workspaceId)?.config?.web?.demoMode === true;
}

/**
 * The local data scope: data version, user and workspace, with a demo segment when the page boots in demo mode.
 *
 * With demo mode off the string is exactly what it was before demo mode existed, so no existing cache is invalidated.
 */
export function localDataScope(version: string, userId: string | null, workspaceId: string | null, demo: boolean) {
  const scope = `${version}-${userId}-${workspaceId}`;
  return demo ? `${scope}-${DEMO_SCOPE_SEGMENT}` : scope;
}

/** What the store needs to label person names while a page runs in demo mode. */
export type DemoLabelling = {
  /** The boot workspace: an account's role is its membership's role here. */
  workspaceId: string;
  /** The workspace's `commandAliasChannelNames`, alias key to the channel name drivers see. */
  aliasChannelNames: Readonly<Record<string, string>>;
};

let labelling: DemoLabelling | null = null;

/**
 * Turns labeling on for the page's life, or leaves it off with null. Set once, from the boot value `store/scope.ts`
 * exports, so the store's writes match the local data scope they are kept under.
 */
export function startDemoLabelling(value: DemoLabelling | null): void {
  labelling = value;
}

/** The labeling the store applies to person names, or null when the page did not boot in demo mode. */
export function demoLabelling(): DemoLabelling | null {
  return labelling;
}
